import { defineStore } from "pinia";
import { ref } from "vue";
import type { GetTenantCollectionQuery, GetAllTenantCollectionItemsQuery } from "@/graphql";

export const useCollectionsProvider = defineStore("CollectionsProvider", () => {
  const collections = ref<GetTenantCollectionQuery["tenantCollection"]>([]);
  const items = ref<GetAllTenantCollectionItemsQuery["tenantCollectionItem"]>([]);

  function getAll(collectionType?: string) {
    if (!collectionType) return collections.value;
    return collections.value.filter((c) => c.collectionType === collectionType);
  }

  function getCollectionsForMaterial(materialInternalId: string): string[] {
    return items.value.filter((x) => x.materialInternalId === materialInternalId).map((x) => String(x.tenantCollectionId));
  }

  function getMaterialIdsForCollections(collectionIds: string[]): string[] {
    return [
      ...new Set(
        items.value.filter((x) => collectionIds.includes(String(x.tenantCollectionId))).map((x) => x.materialInternalId)
      ),
    ];
  }

  function mapCollections(data: GetTenantCollectionQuery) {
    collections.value = data.tenantCollection ?? [];
  }

  function mapItems(data: GetAllTenantCollectionItemsQuery) {
    items.value = data.tenantCollectionItem ?? [];
  }

  function addCollection(collection: { id: string; name: string; tenantId: string; collectionType: string }) {
    collections.value = [...collections.value, collection];
  }

  function updateCollection(id: string, name: string) {
    collections.value = collections.value.map((c) => (c.id === id ? { ...c, name } : c));
  }

  function removeCollection(id: string) {
    collections.value = collections.value.filter((c) => c.id !== id);
    items.value = items.value.filter((i) => i.tenantCollectionId !== id);
  }

  function addItem(materialInternalId: string, tenantCollectionId: string) {
    items.value = [...items.value, { materialInternalId, tenantCollectionId }];
  }

  function removeItem(materialInternalId: string, tenantCollectionId: string) {
    items.value = items.value.filter(
      (i) => !(i.materialInternalId === materialInternalId && i.tenantCollectionId === tenantCollectionId)
    );
  }

  return {
    collections,
    getAll,
    getCollectionsForMaterial,
    getMaterialIdsForCollections,
    mapCollections,
    mapItems,
    addCollection,
    updateCollection,
    removeCollection,
    addItem,
    removeItem,
    items,
  };
});
